import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, Files, FileUp, Pause, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

type ContractStatus = 'ACTIVE' | 'SUSPENDED' | 'CANCELLED' | 'CLOSED';

interface Contract {
  id: string;
  codigoContrato: string;
  dataContrato: string;
  vigenciaInicio: string;
  vigenciaFim: string;
  clientId: string;
  status: ContractStatus;
  valor: string | number;
  referencia: string;
  diaVencimento: number;
  vencimentoInicial: string;
  numeroParcelas: number;
  client?: { id: string; razaoSocial: string };
}

interface ListResponse {
  rows: Contract[];
  total: number;
}

const STATUS_LABEL: Record<ContractStatus, string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
  CLOSED: 'Encerrado',
};
const STATUS_STYLE: Record<ContractStatus, string> = {
  ACTIVE: 'bg-emerald-500/10 text-emerald-600',
  SUSPENDED: 'bg-amber-500/10 text-amber-600',
  CANCELLED: 'bg-destructive/10 text-destructive',
  CLOSED: 'bg-muted text-muted-foreground',
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

export function ContractsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [adjusting, setAdjusting] = useState<Contract | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['contracts', { statusFilter, search }],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '200' });
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (search) params.set('search', search);
      return apiFetch<ListResponse>(`/contracts?${params}`);
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => apiFetch<Contract>(`/contracts/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => apiFetch<Contract>(`/contracts/${id}/suspend`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/contracts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const eligibleForRenew = (data?.rows ?? []).filter(
    (c) => selected.has(c.id) && c.status === 'ACTIVE',
  );

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Contratos</h1>
          <p className="text-sm text-muted-foreground">
            Geram parcelas automaticamente no financeiro ao serem criados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
              >
                <FileUp className="h-4 w-4" /> CSV
              </button>
              <button
                type="button"
                disabled={eligibleForRenew.length === 0}
                onClick={() => setShowRenew(true)}
                className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" /> Renovar ({eligibleForRenew.length})
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          placeholder="Buscar por código ou referência…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ContractStatus | 'ALL')}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="ALL">Todos</option>
          {(Object.keys(STATUS_LABEL) as ContractStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {isError && <p className="p-4 text-sm text-destructive">Erro ao carregar.</p>}
        {data && data.rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <Files className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum contrato.</p>
          </div>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-2 py-2 w-8"></th>
                <th className="px-4 py-2 font-medium">Código</th>
                <th className="px-4 py-2 font-medium">Cliente</th>
                <th className="px-4 py-2 font-medium">Referência</th>
                <th className="px-4 py-2 font-medium">Vigência</th>
                <th className="px-4 py-2 font-medium">Valor</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      disabled={c.status !== 'ACTIVE'}
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{c.codigoContrato}</td>
                  <td className="px-4 py-2 font-medium">{c.client?.razaoSocial ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground truncate max-w-xs">
                    {c.referencia}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {fmtDate(c.vigenciaInicio)} → {fmtDate(c.vigenciaFim)}
                  </td>
                  <td className="px-4 py-2">{formatBRL(Number(c.valor))}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[c.status]}`}
                    >
                      {STATUS_LABEL[c.status]}
                    </span>
                  </td>
                  <td className="px-2 py-2 flex justify-end gap-1">
                    {canManage && c.status === 'ACTIVE' && (
                      <>
                        <button
                          type="button"
                          data-dica="Editar"
                          onClick={() => setEditing(c)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Reajustar parcelas em aberto"
                          onClick={() => setAdjusting(c)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <Calculator className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Suspender"
                          onClick={() => suspend.mutate(c.id)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                        >
                          <Pause className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-dica="Cancelar (e cancelar parcelas futuras)"
                          onClick={() => {
                            if (confirm('Cancelar contrato? Parcelas pendentes serão canceladas.')) {
                              cancel.mutate(c.id);
                            }
                          }}
                          className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {canManage && (c.status === 'CANCELLED' || c.status === 'CLOSED') && (
                      <button
                        type="button"
                        data-dica="Excluir"
                        onClick={() => {
                          if (confirm('Excluir contrato?')) remove.mutate(c.id);
                        }}
                        className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showCreate && (
        <ContractFormDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['contracts'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
      {editing && (
        <ContractFormDialog
          contract={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['contracts'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
      {adjusting && (
        <AdjustInstallmentsDialog
          contract={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => {
            setAdjusting(null);
            qc.invalidateQueries({ queryKey: ['contracts'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
      {showImport && (
        <ImportCsvDialog
          onClose={() => setShowImport(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['contracts'] })}
        />
      )}
      {showRenew && (
        <RenewDialog
          contractIds={eligibleForRenew.map((c) => c.id)}
          contracts={eligibleForRenew}
          onClose={() => setShowRenew(false)}
          onDone={() => {
            setShowRenew(false);
            setSelected(new Set());
            qc.invalidateQueries({ queryKey: ['contracts'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
    </div>
  );
}

function ContractFormDialog({
  contract,
  onClose,
  onSaved,
}: {
  contract?: Contract;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!contract;
  const today = new Date().toISOString().slice(0, 10);
  const toDateInput = (iso: string | undefined): string => {
    if (!iso) return today;
    try {
      return new Date(iso).toISOString().slice(0, 10);
    } catch {
      return today;
    }
  };
  const [form, setForm] = useState({
    codigoContrato: contract?.codigoContrato ?? '',
    clientId: contract?.clientId ?? '',
    issuerCompanyId: (contract as Contract & { issuerCompanyId?: string | null })?.issuerCompanyId ?? '',
    referencia: contract?.referencia ?? '',
    valor: contract ? Number(contract.valor) : 0,
    dataContrato: toDateInput(contract?.dataContrato),
    vigenciaInicio: toDateInput(contract?.vigenciaInicio),
    vigenciaFim: toDateInput(contract?.vigenciaFim),
    diaVencimento: contract?.diaVencimento ?? 10,
    vencimentoInicial: toDateInput(contract?.vencimentoInicial),
    numeroParcelas: contract?.numeroParcelas ?? 12,
  });
  const [error, setError] = useState<string | null>(null);

  const clients = useQuery({
    queryKey: ['clients', 'select'],
    queryFn: () =>
      apiFetch<{ rows: Array<{ id: string; razaoSocial: string }> }>('/clients?pageSize=200').then(
        (r) => r.rows,
      ),
  });

  const issuers = useQuery({
    queryKey: ['issuer-companies'],
    queryFn: () =>
      apiFetch<Array<{ id: string; razaoSocial: string; isDefault: boolean }>>(
        '/issuer-companies',
      ),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        ...form,
        issuerCompanyId: form.issuerCompanyId || undefined,
      });
      return isEdit
        ? apiFetch<Contract>(`/contracts/${contract!.id}`, { method: 'PATCH', body })
        : apiFetch<Contract>('/contracts', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar contrato ${contract!.codigoContrato}` : 'Novo contrato'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Código *">
            <input
              required
              className="input"
              value={form.codigoContrato}
              onChange={(e) => setForm((s) => ({ ...s, codigoContrato: e.target.value }))}
            />
          </Field>
          <Field label="Cliente *">
            <select
              required
              disabled={isEdit}
              className="input disabled:opacity-60 disabled:cursor-not-allowed"
              value={form.clientId}
              onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
            >
              <option value="">—</option>
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Empresa emissora">
            <select
              className="input"
              value={form.issuerCompanyId}
              onChange={(e) => setForm((s) => ({ ...s, issuerCompanyId: e.target.value }))}
            >
              <option value="">— Usar padrão —</option>
              {issuers.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razaoSocial}
                  {c.isDefault ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Referência *">
            <input
              required
              className="input md:col-span-2"
              value={form.referencia}
              onChange={(e) => setForm((s) => ({ ...s, referencia: e.target.value }))}
            />
          </Field>
          <Field label="Valor total *">
            <input
              required
              type="number"
              step="0.01"
              min={0.01}
              className="input"
              value={form.valor || ''}
              onChange={(e) => setForm((s) => ({ ...s, valor: Number(e.target.value) || 0 }))}
            />
          </Field>
          <Field label="Número de parcelas *">
            <input
              required
              type="number"
              min={1}
              max={120}
              disabled={isEdit}
              className="input disabled:opacity-60 disabled:cursor-not-allowed"
              value={form.numeroParcelas}
              onChange={(e) =>
                setForm((s) => ({ ...s, numeroParcelas: Number(e.target.value) || 1 }))
              }
            />
          </Field>
          <Field label="Data do contrato">
            <input
              type="date"
              className="input"
              value={form.dataContrato}
              onChange={(e) => setForm((s) => ({ ...s, dataContrato: e.target.value }))}
            />
          </Field>
          <Field label="Vigência início">
            <input
              type="date"
              className="input"
              value={form.vigenciaInicio}
              onChange={(e) => setForm((s) => ({ ...s, vigenciaInicio: e.target.value }))}
            />
          </Field>
          <Field label="Vigência fim">
            <input
              type="date"
              className="input"
              value={form.vigenciaFim}
              onChange={(e) => setForm((s) => ({ ...s, vigenciaFim: e.target.value }))}
            />
          </Field>
          <Field label="Vencimento da 1ª parcela">
            <input
              type="date"
              className="input"
              value={form.vencimentoInicial}
              onChange={(e) => setForm((s) => ({ ...s, vencimentoInicial: e.target.value }))}
            />
          </Field>
          <Field label="Dia de vencimento">
            <input
              type="number"
              min={1}
              max={28}
              className="input"
              value={form.diaVencimento}
              onChange={(e) =>
                setForm((s) => ({ ...s, diaVencimento: Number(e.target.value) || 10 }))
              }
            />
          </Field>
        </div>

        <div className="rounded-md bg-muted/30 p-3 text-sm">
          Valor por parcela:{' '}
          <strong>
            {formatBRL(
              form.numeroParcelas > 0
                ? Math.round((form.valor / form.numeroParcelas) * 100) / 100
                : 0,
            )}
          </strong>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending
              ? isEdit
                ? 'Salvando…'
                : 'Criando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Criar contrato e parcelas'}
          </button>
        </div>
      </form>

      <Style />
    </Dialog>
  );
}

interface AdjustResult {
  affected: number;
  oldValorParcela: number | null;
  novoValorParcela: number;
  novoValorContrato: number;
}

interface ContractDetail extends Contract {
  financials?: Array<{
    id: string;
    valor: string | number;
    status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
    dataVencimento: string;
    numeroParcela: number | null;
    totalParcelas: number | null;
  }>;
}

function AdjustInstallmentsDialog({
  contract,
  onClose,
  onSaved,
}: {
  contract: Contract;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<'percent' | 'absolute'>('percent');
  const [taxa, setTaxa] = useState<number>(10);
  const [novoValor, setNovoValor] = useState<number>(0);
  const [result, setResult] = useState<AdjustResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['contract', contract.id, 'detail'],
    queryFn: () => apiFetch<ContractDetail>(`/contracts/${contract.id}`),
  });

  const open = (detail.data?.financials ?? []).filter(
    (f) => f.status === 'PENDING' || f.status === 'OVERDUE',
  );
  const paid = (detail.data?.financials ?? []).filter((f) => f.status === 'PAID');
  const valorAtualParcela = open.length > 0 ? Number(open[0]!.valor) : 0;

  const preview =
    mode === 'percent'
      ? Math.round(valorAtualParcela * (1 + taxa / 100) * 100) / 100
      : Math.round(novoValor * 100) / 100;
  const novoTotalContrato =
    paid.reduce((acc, p) => acc + Number(p.valor), 0) + open.length * preview;

  const apply = useMutation({
    mutationFn: () =>
      apiFetch<AdjustResult>(`/contracts/${contract.id}/adjust-installments`, {
        method: 'POST',
        body: JSON.stringify(
          mode === 'percent' ? { taxaReajuste: taxa } : { novoValorParcela: novoValor },
        ),
      }),
    onSuccess: (r) => setResult(r),
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  return (
    <Dialog title={`Reajustar parcelas em aberto — ${contract.codigoContrato}`} onClose={onClose}>
      {result ? (
        <div className="space-y-4">
          <div className="rounded-md bg-emerald-500/10 p-4 text-sm text-emerald-700">
            <p>
              <strong>{result.affected}</strong> parcela(s) reajustada(s) para{' '}
              <strong>{formatBRL(result.novoValorParcela)}</strong>.
            </p>
            <p className="mt-1 text-xs">
              Novo valor total do contrato: <strong>{formatBRL(result.novoValorContrato)}</strong>.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSaved}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Concluir
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            setError(null);
            apply.mutate();
          }}
          className="space-y-4"
        >
          {detail.isLoading && (
            <p className="text-sm text-muted-foreground">Carregando parcelas…</p>
          )}
          {detail.data && (
            <div className="grid grid-cols-3 gap-3 rounded-md bg-muted/30 p-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Parcelas pagas</p>
                <p className="font-medium">{paid.length}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Em aberto</p>
                <p className="font-medium">{open.length}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Valor atual da parcela</p>
                <p className="font-medium">{formatBRL(valorAtualParcela)}</p>
              </div>
            </div>
          )}

          {open.length === 0 && detail.data && (
            <p className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700">
              Não há parcelas em aberto para reajustar.
            </p>
          )}

          {open.length > 0 && (
            <>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'percent'}
                    onChange={() => setMode('percent')}
                  />
                  Reajuste por percentual (%)
                </label>
                {mode === 'percent' && (
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={taxa}
                    onChange={(e) => setTaxa(Number(e.target.value) || 0)}
                    autoFocus
                  />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={mode === 'absolute'}
                    onChange={() => setMode('absolute')}
                  />
                  Definir novo valor de parcela (R$)
                </label>
                {mode === 'absolute' && (
                  <input
                    type="number"
                    step="0.01"
                    min={0.01}
                    className="input"
                    value={novoValor || ''}
                    onChange={(e) => setNovoValor(Number(e.target.value) || 0)}
                  />
                )}
              </div>

              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>Nova parcela:</span>
                  <strong>{formatBRL(preview)}</strong>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {open.length} parcela(s) × {formatBRL(preview)}
                  </span>
                  <span>
                    Novo total do contrato: <strong>{formatBRL(novoTotalContrato)}</strong>
                  </span>
                </div>
              </div>
            </>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={apply.isPending || open.length === 0 || preview <= 0}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {apply.isPending ? 'Aplicando…' : 'Aplicar reajuste'}
            </button>
          </div>
        </form>
      )}
      <Style />
    </Dialog>
  );
}

function RenewDialog({
  contractIds,
  contracts,
  onClose,
  onDone,
}: {
  contractIds: string[];
  contracts: Contract[];
  onClose: () => void;
  onDone: () => void;
}) {
  type Mode = 'percent' | 'parcela' | 'total' | 'none';
  const [form, setForm] = useState({
    novaVigenciaFim: '',
    mode: 'percent' as Mode,
    taxaReajuste: 10,
    novoValorParcela: 0,
    novoValor: 0,
  });
  const [result, setResult] = useState<{
    renewed: number;
    issues: Array<{ contractId: string; reason: string }>;
  } | null>(null);

  // Preview do reajuste sobre os contratos selecionados.
  const selected = contracts.filter((c) => contractIds.includes(c.id));
  const previewRows = selected.map((c) => {
    const total = Number(c.valor);
    const parcela = c.numeroParcelas > 0 ? total / c.numeroParcelas : 0;
    let novoTotal = total;
    if (form.mode === 'percent') novoTotal = total * (1 + form.taxaReajuste / 100);
    else if (form.mode === 'parcela')
      novoTotal = form.novoValorParcela * c.numeroParcelas;
    else if (form.mode === 'total') novoTotal = form.novoValor;
    const novaParcela = c.numeroParcelas > 0 ? novoTotal / c.numeroParcelas : 0;
    return {
      id: c.id,
      codigo: c.codigoContrato,
      parcelas: c.numeroParcelas,
      parcelaAtual: Math.round(parcela * 100) / 100,
      parcelaNova: Math.round(novaParcela * 100) / 100,
      totalAtual: Math.round(total * 100) / 100,
      totalNovo: Math.round(novoTotal * 100) / 100,
    };
  });

  const renew = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        contractIds,
        novaVigenciaFim: form.novaVigenciaFim,
      };
      if (form.mode === 'percent') payload.taxaReajuste = form.taxaReajuste;
      else if (form.mode === 'parcela') payload.novoValorParcela = form.novoValorParcela;
      else if (form.mode === 'total') payload.novoValor = form.novoValor;
      return apiFetch<{ renewed: number; issues: Array<{ contractId: string; reason: string }> }>(
        '/contracts/renew',
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },
    onSuccess: (r) => setResult(r),
  });

  return (
    <Dialog title={`Renovar ${contractIds.length} contrato(s)`} onClose={onClose}>
      {result ? (
        <div className="space-y-4">
          <p className="text-sm">
            <strong>{result.renewed}</strong> renovado(s).{' '}
            {result.issues.length > 0 && `${result.issues.length} com problema.`}
          </p>
          {result.issues.length > 0 && (
            <details className="text-xs">
              <summary>Detalhes</summary>
              <ul className="mt-2 list-disc pl-4">
                {result.issues.map((i, idx) => (
                  <li key={idx} className="font-mono">
                    {i.contractId.slice(0, 8)}… — {i.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                onDone();
              }}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Concluir
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            renew.mutate();
          }}
          className="space-y-4"
        >
          <Field label="Nova vigência fim *">
            <input
              required
              type="date"
              className="input"
              value={form.novaVigenciaFim}
              onChange={(e) => setForm((s) => ({ ...s, novaVigenciaFim: e.target.value }))}
            />
          </Field>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={form.mode === 'percent'}
                onChange={() => setForm((s) => ({ ...s, mode: 'percent' }))}
              />
              Reajuste por percentual (%)
            </label>
            {form.mode === 'percent' && (
              <input
                type="number"
                step="0.0001"
                className="input"
                value={form.taxaReajuste}
                onChange={(e) =>
                  setForm((s) => ({ ...s, taxaReajuste: Number(e.target.value) || 0 }))
                }
              />
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={form.mode === 'parcela'}
                onChange={() => setForm((s) => ({ ...s, mode: 'parcela' }))}
              />
              Definir novo valor por parcela (R$)
            </label>
            {form.mode === 'parcela' && (
              <input
                type="number"
                step="0.01"
                min={0.01}
                className="input"
                value={form.novoValorParcela || ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, novoValorParcela: Number(e.target.value) || 0 }))
                }
              />
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={form.mode === 'total'}
                onChange={() => setForm((s) => ({ ...s, mode: 'total' }))}
              />
              Definir novo valor total
            </label>
            {form.mode === 'total' && (
              <input
                type="number"
                step="0.01"
                min={0.01}
                className="input"
                value={form.novoValor || ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, novoValor: Number(e.target.value) || 0 }))
                }
              />
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={form.mode === 'none'}
                onChange={() => setForm((s) => ({ ...s, mode: 'none' }))}
              />
              Sem reajuste (manter valor atual)
            </label>
          </div>

          {previewRows.length > 0 && (
            <details open className="rounded-md border border-border bg-muted/20">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                Preview ({previewRows.length} contrato{previewRows.length > 1 ? 's' : ''})
              </summary>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="border-y border-border bg-muted/40 text-left">
                    <tr>
                      <th className="px-2 py-1 font-medium">Contrato</th>
                      <th className="px-2 py-1 font-medium text-right">Parcela atual</th>
                      <th className="px-2 py-1 font-medium text-right">Parcela nova</th>
                      <th className="px-2 py-1 font-medium text-right">Total novo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r) => (
                      <tr key={r.id} className="border-b border-border last:border-0">
                        <td className="px-2 py-1 font-mono">{r.codigo}</td>
                        <td className="px-2 py-1 text-right">{formatBRL(r.parcelaAtual)}</td>
                        <td className="px-2 py-1 text-right font-medium">
                          {formatBRL(r.parcelaNova)}
                        </td>
                        <td className="px-2 py-1 text-right">{formatBRL(r.totalNovo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={renew.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {renew.isPending ? 'Renovando…' : 'Renovar'}
            </button>
          </div>
        </form>
      )}
      <Style />
    </Dialog>
  );
}

function ImportCsvDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number; issues: Array<{ line: number; field: string; message: string }> } | null>(null);

  const upload = useMutation({
    mutationFn: (content: string) =>
      apiFetch<typeof result extends infer R ? Exclude<R, null> : never>('/contracts/import', {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: content,
      }),
    onSuccess: (r) => {
      setResult(r as unknown as Exclude<typeof result, null>);
      onDone();
    },
  });

  return (
    <Dialog title="Importar contratos (CSV)" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Separador <code>;</code>. Colunas: <code>codigo_contrato, codcliente OR client_id,
          referencia, valor, vigencia_inicio, vigencia_fim, dia_vencimento,
          vencimento_inicial, numero_parcelas, data_contrato</code>.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(await f.text());
          }}
          className="block text-sm"
        />
        {upload.isPending && <p className="text-sm text-muted-foreground">Importando…</p>}
        {result && (
          <div className="rounded-md bg-muted/30 p-3 text-sm">
            <p>
              <strong>{result.imported}</strong> criados, <strong>{result.skipped}</strong>{' '}
              ignorados.
            </p>
            {result.issues.length > 0 && (
              <details className="mt-2 text-xs">
                <summary>{result.issues.length} issue(s)</summary>
                <ul className="mt-1 list-disc pl-4">
                  {result.issues.slice(0, 20).map((i, idx) => (
                    <li key={idx}>{i.message}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}
